import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
  getDoc,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Project, Client, TimeRecord } from '@/types';

/**
 * プロジェクト一覧を取得（リアルタイム）
 */
export const subscribeToProjects = (
  userId: string,
  status: 'active' | 'archived' | 'completed',
  callback: (projects: Project[]) => void
) => {
  const q = query(
    collection(db, 'users', userId, 'projects'),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const projects = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Project[];
    callback(projects);
  });
};

/**
 * クライアント一覧を取得（リアルタイム）
 */
export const subscribeToClients = (
  userId: string,
  callback: (clients: Client[]) => void
) => {
  const q = query(
    collection(db, 'users', userId, 'clients'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const clients = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Client[];
    callback(clients);
  });
};

/**
 * アクティブな出勤記録を取得（リアルタイム）
 */
export const subscribeToActiveClockIn = (
  userId: string,
  callback: (record: TimeRecord | null) => void
) => {
  const q = query(
    collection(db, 'users', userId, 'timestamps'),
    where('status', '==', 'active'),
    limit(1)
  );

  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
    } else {
      const record = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      } as TimeRecord;
      callback(record);
    }
  });
};

/**
 * 最近のタイムレコードを取得（リアルタイム）
 */
export const subscribeToRecentTimeRecords = (
  userId: string,
  limitCount: number,
  callback: (records: TimeRecord[]) => void
) => {
  const q = query(
    collection(db, 'users', userId, 'timestamps'),
    orderBy('clockInTime', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(q, (snapshot) => {
    const records = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as TimeRecord[];
    callback(records);
  });
};

/**
 * 出勤処理
 */
export const clockIn = async (
  userId: string,
  project: { id: string; name: string; code?: string; client?: any }
): Promise<string> => {
  const docRef = await addDoc(collection(db, 'users', userId, 'timestamps'), {
    userId,
    projectId: project.id,
    project: {
      id: project.id,
      name: project.name,
      code: project.code || '',
      client: project.client || null,
    },
    clockInTime: serverTimestamp(),
    clockOutTime: null,
    totalMinutes: null,
    status: 'active',
    memo: '',
    categories: [],
    recordType: 'clock', // 🆕 打刻
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
};

/**
 * 🆕 プロジェクト未選択で出勤
 */
export const clockInWithoutProject = async (userId: string): Promise<string> => {
  const docRef = await addDoc(collection(db, 'users', userId, 'timestamps'), {
    userId,
    projectId: null,
    project: null,
    clockInTime: serverTimestamp(),
    clockOutTime: null,
    totalMinutes: null,
    status: 'active',
    memo: '',
    categories: [],
    recordType: 'clock', // 🆕 打刻
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
};

/**
 * 🆕 プロジェクトの後付け（後からプロジェクトを紐付ける）
 */
export const assignProjectToRecord = async (
  userId: string,
  recordId: string,
  project: { id: string; name: string; code?: string; client?: any }
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'timestamps', recordId);
  await updateDoc(docRef, {
    projectId: project.id,
    project: {
      id: project.id,
      name: project.name,
      code: project.code || '',
      client: project.client || null,
    },
    updatedAt: serverTimestamp(),
  });
};

/**
 * 退勤処理
 */
export const clockOut = async (userId: string, recordId: string): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'timestamps', recordId);
  const now = Timestamp.now();

  // レコードを取得してtotalMinutesを計算
  const recordSnap = await getDoc(docRef);
  if (!recordSnap.exists()) {
    throw new Error('Record not found');
  }

  const data = recordSnap.data();
  const clockInTime = data.clockInTime as Timestamp;
  const diffMs = now.toMillis() - clockInTime.toMillis();
  const totalMinutes = Math.floor(diffMs / 60000);

  await updateDoc(docRef, {
    clockOutTime: now,
    totalMinutes,
    status: 'completed',
    updatedAt: serverTimestamp(),
  });
};

/**
 * タイムレコードの更新（メモ・カテゴリ）
 */
export const updateTimeRecord = async (
  userId: string,
  recordId: string,
  data: { memo?: string; categories?: string[] }
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'timestamps', recordId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

/**
 * 🆕 タイムレコードの詳細編集（日時、プロジェクト含む全フィールド編集可能）
 */
export const updateTimeRecordDetails = async (
  userId: string,
  recordId: string,
  data: {
    clockInTime?: Date;
    clockOutTime?: Date;
    memo?: string;
    notes?: string;
    categories?: string[];
    projectId?: string;
    project?: {
      id: string;
      name: string;
      code?: string;
      client?: any;
    };
  },
  editedBy: string
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'timestamps', recordId);
  const updateData: any = {
    updatedAt: serverTimestamp(),
  };

  // 日時の変換
  if (data.clockInTime) {
    updateData.clockInTime = Timestamp.fromDate(data.clockInTime);
  }
  if (data.clockOutTime) {
    updateData.clockOutTime = Timestamp.fromDate(data.clockOutTime);
  }

  // その他のフィールド
  if (data.memo !== undefined) updateData.memo = data.memo;
  if (data.notes !== undefined) updateData.notes = data.notes;
  if (data.categories !== undefined) updateData.categories = data.categories;
  if (data.projectId !== undefined) updateData.projectId = data.projectId;
  if (data.project !== undefined) updateData.project = data.project;

  // totalMinutes の再計算（clockInTime と clockOutTime が両方ある場合）
  if (updateData.clockInTime && updateData.clockOutTime) {
    const diffMs = updateData.clockOutTime.toMillis() - updateData.clockInTime.toMillis();
    updateData.totalMinutes = Math.floor(diffMs / 60000);
  }

  // 編集履歴を記録
  const currentDoc = await getDoc(docRef);
  if (currentDoc.exists()) {
    const currentData = currentDoc.data();
    const history = currentData.editHistory || [];
    
    // 変更されたフィールドのみ記録
    const changedFields: any = {};
    if (data.clockInTime) changedFields.clockInTime = true;
    if (data.clockOutTime) changedFields.clockOutTime = true;
    if (data.projectId !== undefined) changedFields.projectId = true;
    if (data.categories !== undefined) changedFields.categories = true;
    if (data.memo !== undefined) changedFields.memo = true;
    
    history.push({
      editedAt: Timestamp.now(),
      editedBy: editedBy,
      changes: changedFields,
    });
    updateData.editHistory = history;
  }

  await updateDoc(docRef, updateData);
};

/**
 * 🔧 マイグレーション: totalMinutesが0または未設定のレコードを修正
 */
export const fixTotalMinutes = async (userId: string): Promise<number> => {
  const timestampsRef = collection(db, 'users', userId, 'timestamps');
  const q = query(timestampsRef, where('status', '==', 'completed'));
  
  const snapshot = await getDocs(q);
  let fixedCount = 0;

  const batch = writeBatch(db);
  
  snapshot.docs.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    const totalMinutes = data.totalMinutes || 0;
    
    // totalMinutesが0で、clockInTimeとclockOutTimeがある場合
    if (totalMinutes === 0 && data.clockInTime && data.clockOutTime) {
      const clockInMs = data.clockInTime.toMillis();
      const clockOutMs = data.clockOutTime.toMillis();
      const calculatedMinutes = Math.floor((clockOutMs - clockInMs) / 60000);
      
      if (calculatedMinutes > 0) {
        batch.update(docSnapshot.ref, {
          totalMinutes: calculatedMinutes,
          updatedAt: serverTimestamp(),
        });
        fixedCount++;
      }
    }
  });

  if (fixedCount > 0) {
    await batch.commit();
  }

  return fixedCount;
};

/**
 * 稼働時間の集計（今日）
 */
export const getTodayWorkingMinutes = (
  userId: string,
  callback: (minutes: number) => void
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const todayTimestamp = Timestamp.fromDate(today);
  const tomorrowTimestamp = Timestamp.fromDate(tomorrow);


  const q = query(
    collection(db, 'users', userId, 'timestamps'),
    where('clockInTime', '>=', todayTimestamp),
    where('clockInTime', '<', tomorrowTimestamp)
  );

  return onSnapshot(q, (snapshot) => {
    let totalMinutes = 0;
    const now = Date.now();
    
    
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      
      if (data.status === 'completed') {
        // 完了済みレコード: totalMinutesまたは計算値を使用
        // マイナス値や不正値は 0 として扱う
        const minutes = data.totalMinutes;
        if (typeof minutes === 'number' && minutes > 0) {
          totalMinutes += minutes;
        }
      } else if (data.status === 'active' && data.clockInTime) {
        // アクティブレコード: 現在時刻までの経過時間を計算
        const elapsed = now - data.clockInTime.toMillis();
        const minutes = Math.floor(elapsed / 60000);
        // マイナス値は無視（未来の時刻で打刻された場合など）
        if (minutes > 0) {
          totalMinutes += minutes;
        }
      }
    });
    
    callback(totalMinutes);
  });
};

/**
 * 稼働時間の集計（今週）
 */
export const getThisWeekWorkingMinutes = (
  userId: string,
  callback: (minutes: number) => void
) => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 (日曜) - 6 (土曜)
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - dayOfWeek); // 週の始まり（日曜）
  startOfWeek.setHours(0, 0, 0, 0);
  
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6); // 週の終わり（土曜）
  endOfWeek.setHours(23, 59, 59, 999);
  
  const startOfWeekTimestamp = Timestamp.fromDate(startOfWeek);
  const endOfWeekTimestamp = Timestamp.fromDate(endOfWeek);


  const q = query(
    collection(db, 'users', userId, 'timestamps'),
    where('clockInTime', '>=', startOfWeekTimestamp),
    where('clockInTime', '<=', endOfWeekTimestamp)
  );

  return onSnapshot(q, (snapshot) => {
    let totalMinutes = 0;
    const now = Date.now();
    
    
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      
      if (data.status === 'completed') {
        // 完了済みレコード: totalMinutesまたは計算値を使用
        // マイナス値や不正値は 0 として扱う
        const minutes = data.totalMinutes;
        if (typeof minutes === 'number' && minutes > 0) {
          totalMinutes += minutes;
        }
      } else if (data.status === 'active' && data.clockInTime) {
        // アクティブレコード: 現在時刻までの経過時間を計算
        const elapsed = now - data.clockInTime.toMillis();
        const minutes = Math.floor(elapsed / 60000);
        // マイナス値は無視（未来の時刻で打刻された場合など）
        if (minutes > 0) {
          totalMinutes += minutes;
        }
      }
    });
    
    callback(totalMinutes);
  });
};

/**
 * 稼働時間の集計（今月）
 */
export const getThisMonthWorkingMinutes = (
  userId: string,
  callback: (minutes: number) => void
) => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  startOfMonth.setHours(0, 0, 0, 0);
  
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);
  
  const startOfMonthTimestamp = Timestamp.fromDate(startOfMonth);
  const endOfMonthTimestamp = Timestamp.fromDate(endOfMonth);


  const q = query(
    collection(db, 'users', userId, 'timestamps'),
    where('clockInTime', '>=', startOfMonthTimestamp),
    where('clockInTime', '<=', endOfMonthTimestamp)
  );

  return onSnapshot(q, (snapshot) => {
    let totalMinutes = 0;
    const now = Date.now();
    
    
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      
      if (data.status === 'completed') {
        // 完了済みレコード: totalMinutesまたは計算値を使用
        // マイナス値や不正値は 0 として扱う
        const minutes = data.totalMinutes;
        if (typeof minutes === 'number' && minutes > 0) {
          totalMinutes += minutes;
        }
      } else if (data.status === 'active' && data.clockInTime) {
        // アクティブレコード: 現在時刻までの経過時間を計算
        const elapsed = now - data.clockInTime.toMillis();
        const minutes = Math.floor(elapsed / 60000);
        // マイナス値は無視（未来の時刻で打刻された場合など）
        if (minutes > 0) {
          totalMinutes += minutes;
        }
      }
    });
    
    callback(totalMinutes);
  });
};

/**
 * Googleカレンダーイベントを稼働実績として一括登録
 */
export const addCalendarEventsAsTimestamps = async (
  userId: string,
  events: Array<{
    id: string;
    summary: string;
    start: string;
    end: string;
    project?: {
      id: string;
      name: string;
      code: string;
    };
  }>
): Promise<void> => {
  const batch = events.map(async (event) => {
    if (!event.project) return;

    const startTime = Timestamp.fromDate(new Date(event.start));
    const endTime = Timestamp.fromDate(new Date(event.end));
    const diffMs = new Date(event.end).getTime() - new Date(event.start).getTime();
    const totalMinutes = Math.floor(diffMs / 60000);

    await addDoc(collection(db, 'users', userId, 'timestamps'), {
      userId,
      projectId: event.project.id,
      project: {
        id: event.project.id,
        name: event.project.name,
        code: event.project.code,
        client: null,
      },
      clockInTime: startTime,
      clockOutTime: endTime,
      totalMinutes,
      status: 'completed',
      notes: event.summary,
      memo: `Googleカレンダーからインポート: ${event.summary}`,
      categories: ['calendar-import'],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  await Promise.all(batch);
};

/**
 * タイムスタンプを手動で追加（新規レコード作成）
 */
export const addTimestamp = async (
  userId: string,
  data: {
    clockInTime: Timestamp;
    clockOutTime: Timestamp | null;
    projectId?: string;
    project?: {
      id: string;
      name: string;
      code?: string;
      client?: { id: string; name: string } | null;
    };
    categories?: string[];
    memo?: string;
    totalMinutes?: number;
    status: 'active' | 'completed';
    recordType?: 'clock' | 'manual' | 'calendar'; // 🆕 記録タイプ
  }
) => {
  const timestampData = {
    userId,
    projectId: data.projectId || null,
    project: data.project || null,
    clockInTime: data.clockInTime,
    clockOutTime: data.clockOutTime,
    totalMinutes: data.totalMinutes || 0,
    status: data.status,
    categories: data.categories || [],
    memo: data.memo || '',
    notes: data.memo || '',
    recordType: data.recordType || 'clock', // 🆕 デフォルトは 'clock'
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(
    collection(db, 'users', userId, 'timestamps'),
    timestampData
  );

  return docRef.id;
};

/**
 * タイムスタンプを削除（物理削除）
 */
export const deleteTimestamp = async (userId: string, timestampId: string) => {
  const timestampRef = doc(db, 'users', userId, 'timestamps', timestampId);
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(timestampRef);
};
